import {
  ChangeDetectionStrategy, Component, ElementRef, effect, input, model, output, signal,
  untracked, viewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SliderModule } from 'primeng/slider';

export interface Point { x: number; y: number; }
export interface Size { w: number; h: number; }

/** Side of the square crop viewport, in CSS pixels. */
export const CROP_VIEWPORT = 288;

/**
 * The image is laid out at its "cover" size (so it always fills the viewport at
 * zoom 1), centred, then translated by `offset` and scaled by `zoom`. This maps
 * the visible viewport square back onto source-image pixels so the same framing
 * the user sees can be drawn to a canvas.
 */
export function computeCropRect(
  viewport: number, natural: Size, baseScale: number, zoom: number, offset: Point
): { sx: number; sy: number; size: number } {
  const total = baseScale * zoom;
  const left = viewport / 2 + offset.x - (natural.w * total) / 2;
  const top = viewport / 2 + offset.y - (natural.h * total) / 2;
  const size = viewport / total;

  // Clamping guards against sub-pixel drift putting the rect slightly outside the
  // source, which drawImage would render as transparent/black edges.
  return {
    sx: Math.min(Math.max(-left / total, 0), Math.max(natural.w - size, 0)),
    sy: Math.min(Math.max(-top / total, 0), Math.max(natural.h - size, 0)),
    size
  };
}

/** Keeps the image covering the viewport — no empty gutters can be dragged into view. */
export function clampOffset(
  viewport: number, natural: Size, baseScale: number, zoom: number, offset: Point
): Point {
  const total = baseScale * zoom;
  const maxX = Math.max((natural.w * total - viewport) / 2, 0);
  const maxY = Math.max((natural.h * total - viewport) / 2, 0);
  return {
    x: Math.min(Math.max(offset.x, -maxX), maxX),
    y: Math.min(Math.max(offset.y, -maxY), maxY)
  };
}

@Component({
  selector: 'app-image-cropper-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, DialogModule, ButtonModule, SliderModule],
  template: `
    <p-dialog
      [(visible)]="visible"
      [header]="header()"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [dismissableMask]="false"
      [style]="{ width: '25rem' }"
      [breakpoints]="{ '480px': '95vw' }"
      (onHide)="onCancel()">

      <div class="flex flex-col gap-5 items-center">
        <div
          class="relative overflow-hidden bg-surface-900 rounded-lg touch-none select-none
                 cursor-grab active:cursor-grabbing"
          [style.width.px]="viewport"
          [style.height.px]="viewport"
          (pointerdown)="onPointerDown($event)"
          (pointermove)="onPointerMove($event)"
          (pointerup)="onPointerUp($event)"
          (pointercancel)="onPointerUp($event)"
          (wheel)="onWheel($event)">

          @if (imageSrc()) {
            <img
              #sourceImage
              [src]="imageSrc()"
              alt="Crop preview"
              class="absolute top-1/2 left-1/2 max-w-none pointer-events-none"
              [style.width.px]="baseWidth()"
              [style.height.px]="baseHeight()"
              [style.transform]="transform()"
              (load)="onImageLoad($event)" />
          }

          <!-- Mask: dims everything outside the crop area without clipping the image itself -->
          <div
            class="absolute inset-0 pointer-events-none"
            [class.rounded-full]="shape() === 'circle'"
            style="box-shadow: 0 0 0 9999px rgba(0,0,0,0.45); border: 2px solid rgba(255,255,255,0.8);">
          </div>
        </div>

        <div class="flex items-center gap-3 w-full px-1">
          <i class="pi pi-search-minus text-surface-500 text-sm"></i>
          <p-slider
            class="flex-1"
            [ngModel]="zoom()"
            [ngModelOptions]="{ standalone: true }"
            (onChange)="setZoom($event.value ?? 1)"
            [min]="1"
            [max]="4"
            [step]="0.01"
            styleClass="w-full" />
          <i class="pi pi-search-plus text-surface-500 text-sm"></i>
        </div>

        <p class="text-xs text-surface-500 dark:text-surface-400 text-center -mt-2">
          Drag to reposition, scroll or use the slider to zoom.
        </p>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex justify-between items-center w-full">
          <p-button label="Reset" severity="secondary" [text]="true" size="small"
                    icon="pi pi-refresh" (onClick)="reset()" />
          <div class="flex gap-2">
            <p-button label="Cancel" severity="secondary" [outlined]="true" size="small"
                      (onClick)="visible.set(false)" />
            <p-button label="Apply" icon="pi pi-check" size="small"
                      [disabled]="!natural()" (onClick)="apply()" />
          </div>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class ImageCropperDialogComponent {
  visible = model<boolean>(false);
  /** Source image (data URL or URL) to crop. */
  imageSrc = input<string | null>(null);
  header = input<string>('Crop Photo');
  shape = input<'circle' | 'square'>('circle');
  /** Side of the exported square image, in pixels. */
  outputSize = input<number>(512);
  outputMime = input<'image/png' | 'image/jpeg'>('image/jpeg');

  /** Emits the cropped image as a data URL. */
  cropped = output<string>();
  cancelled = output<void>();

  readonly viewport = CROP_VIEWPORT;

  private sourceImage = viewChild<ElementRef<HTMLImageElement>>('sourceImage');

  protected natural = signal<Size | null>(null);
  protected zoom = signal(1);
  protected offset = signal<Point>({ x: 0, y: 0 });

  private drag: { pointerId: number; startX: number; startY: number; origin: Point } | null = null;
  private applied = false;

  constructor() {
    // A newly picked file must start from a clean frame, not the previous image's.
    effect(() => {
      this.imageSrc();
      untracked(() => this.resetState());
    });
  }

  protected baseScale(): number {
    const nat = this.natural();
    if (!nat || !nat.w || !nat.h) return 1;
    return Math.max(this.viewport / nat.w, this.viewport / nat.h);
  }

  protected baseWidth(): number {
    return (this.natural()?.w ?? 0) * this.baseScale();
  }

  protected baseHeight(): number {
    return (this.natural()?.h ?? 0) * this.baseScale();
  }

  protected transform(): string {
    const { x, y } = this.offset();
    return `translate(-50%, -50%) translate(${x}px, ${y}px) scale(${this.zoom()})`;
  }

  protected onImageLoad(event: Event): void {
    const img = event.target as HTMLImageElement;
    this.natural.set({ w: img.naturalWidth, h: img.naturalHeight });
    this.zoom.set(1);
    this.offset.set({ x: 0, y: 0 });
  }

  protected setZoom(value: number): void {
    const nat = this.natural();
    const next = Math.min(Math.max(value ?? 1, 1), 4);
    this.zoom.set(next);
    if (nat) {
      this.offset.set(clampOffset(this.viewport, nat, this.baseScale(), next, this.offset()));
    }
  }

  protected onWheel(event: WheelEvent): void {
    if (!this.natural()) return;
    event.preventDefault();
    this.setZoom(this.zoom() * (event.deltaY < 0 ? 1.08 : 1 / 1.08));
  }

  protected onPointerDown(event: PointerEvent): void {
    if (!this.natural()) return;
    this.drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: this.offset()
    };
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  protected onPointerMove(event: PointerEvent): void {
    const drag = this.drag;
    const nat = this.natural();
    if (!drag || !nat || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    const next = {
      x: drag.origin.x + (event.clientX - drag.startX),
      y: drag.origin.y + (event.clientY - drag.startY)
    };
    this.offset.set(clampOffset(this.viewport, nat, this.baseScale(), this.zoom(), next));
  }

  protected onPointerUp(event: PointerEvent): void {
    if (this.drag?.pointerId === event.pointerId) {
      (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);
      this.drag = null;
    }
  }

  protected reset(): void {
    this.zoom.set(1);
    this.offset.set({ x: 0, y: 0 });
  }

  protected apply(): void {
    const result = this.renderCrop();
    if (!result) return;
    this.applied = true;
    this.cropped.emit(result);
    this.visible.set(false);
  }

  /** p-dialog fires onHide for Apply too — only Apply-less closes count as a cancel. */
  protected onCancel(): void {
    if (!this.applied) this.cancelled.emit();
    this.applied = false;
    this.resetState();
  }

  private resetState(): void {
    this.natural.set(null);
    this.zoom.set(1);
    this.offset.set({ x: 0, y: 0 });
    this.drag = null;
  }

  private renderCrop(): string | null {
    const nat = this.natural();
    // Draw from the element already in the DOM — it has decoded (onImageLoad set
    // `natural`), so drawImage is safe synchronously. A fresh `new Image()` from
    // the same data URL would not be, and would silently draw nothing.
    const image = this.sourceImage()?.nativeElement;
    if (!nat || !image) return null;

    const size = this.outputSize();
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const mime = this.outputMime();
    if (mime === 'image/jpeg') {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
    }

    const rect = computeCropRect(this.viewport, nat, this.baseScale(), this.zoom(), this.offset());
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, rect.sx, rect.sy, rect.size, rect.size, 0, 0, size, size);

    try {
      return canvas.toDataURL(mime, 0.92);
    } catch {
      // Cross-origin source taints the canvas — nothing can be exported from it.
      return null;
    }
  }
}
